import React, { useState } from 'react';
import {
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AliasFormInterface, columnsAlias } from '../AliasConstants';
import TableWrapper from '../../../../components/Table/TableWrapper';
import TableHeadComp from '../../../../components/Table/TableHead.tsx/TableHeadComp';
import { Order } from '../../../../models/General';
import { StyledTableRow } from '../../../../components/LinksTable/LinksTable';
import { TableCellCustom } from '../../../../components/tableItems';
import useAliasStore from '../../../../stores/alias/useAliasStore';
import { AliasResultsI } from '../../../../models/Alias';

interface AliasTableInterface {
  aliasTableResults: AliasResultsI[];
  count: number;
  fetchAlias: () => void;
  viewAlias: (obj: AliasFormInterface, id: number) => void;
}

const AliasTable: React.FC<AliasTableInterface> = ({
  aliasTableResults,
  count,
  fetchAlias,
  viewAlias,
}) => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('smtp-alias');
  const {
    loadingAlias,
    updateLoadingAlias,
    aliasRequestObject,
    updateAliasRequest,
  } = useAliasStore();

  const handleRequestSort = (property: string) => {
    const isSame = orderBy === property;
    let orderRequest = order;
    if (isSame) {
      orderRequest = order === 'asc' ? 'desc' : 'asc';
      setOrder(order === 'asc' ? 'desc' : 'asc');
    }
    setOrderBy(property);
    updateAliasRequest({
      ordering: `${orderRequest === 'desc' ? '-' : ''}${property}`,
    });
  };

  const updatePage = (event: unknown, newPage: number) => {
    updateLoadingAlias(true);
    updateAliasRequest({ page: newPage });
    window.scrollTo(0, 0);
  };

  const updatePageSize = (value: number) => {
    updateLoadingAlias(true);
    updateAliasRequest({ page: 1, page_size: value });
    window.scrollTo(0, 0);
  };

  return (
    <TableWrapper
      loading={loadingAlias}
      error={false}
      type="alias"
      totalCells={4}
      itemsLength={aliasTableResults.length}
      fetchInfo={fetchAlias}
      countItems={count}
      pageSize={aliasRequestObject.page_size}
      page={aliasRequestObject.page}
      handleChangePage={updatePage}
      handleChangeRowsPerPage={updatePageSize}
      HeadOfTable={
        <TableHead sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
          <TableRow>
            {columnsAlias.map(headCell => (
              <TableCell
                key={headCell.id}
                sx={{
                  minWidth: 80,
                  color: 'black',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
                align="left"
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  disabled={headCell.disableSort}
                  direction={order}
                  onClick={() => {
                    handleRequestSort(headCell.id);
                  }}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      }
    >
      {aliasTableResults.map((item, index) => {
        const labelId = `alias-${index}`;
        return (
          <StyledTableRow
            hover
            role="checkbox"
            key={item.id}
            selected={false}
            sx={{ cursor: !loadingAlias ? 'pointer' : 'default' }}
            className="column-row"
            onClick={() => {
              if (loadingAlias) return;
              viewAlias(
                {
                  smtp_alias: item.smtp_alias,
                  user_id: item.user.id,
                  site_id: item.site ? item.site.id : 0,
                  is_active: item.is_active,
                },
                item.id,
              );
            }}
          >
            <TableCell
              component="th"
              className="first_column"
              scope="row"
              sx={{ backgroundColor: index % 2 === 0 ? '#eeeeee' : '' }}
              padding="none"
              id={labelId}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 2,
                  paddingBottom: 2,
                  paddingX: 1,
                  minWidth: 150,
                  maxWidth: 450,
                }}
              >
                <Typography
                  color="rgba(0, 0, 0, 0.4)"
                  fontWeight={500}
                  fontSize={16}
                  letterSpacing={0.5}
                  textAlign="center"
                >
                  {item.smtp_alias}
                </Typography>
              </Box>
            </TableCell>
            <TableCellCustom>{item.user.username ?? ''}</TableCellCustom>
            <TableCellCustom>{item.site ? item.site.name : ''}</TableCellCustom>

            <TableCellCustom>
              {item.is_active ? <CheckIcon /> : <CloseIcon />}
            </TableCellCustom>
          </StyledTableRow>
        );
      })}
    </TableWrapper>
  );
};

export default AliasTable;
